import { Http } from '@mygpt/app/http';

// Skill接口定义（基于API文档）
export interface Skill {
  name: string;
  description: string;
  user_skill: boolean; // 是否为用户上传的技能
  enabled: boolean; // 是否已启用（根据机器人配置自动标记）
  category?: string; // 技能分类
}

// 技能列表响应接口
export interface SkillListResponse {
  skills: Skill[];
  total: number;
}

// 上传技能响应接口
export interface UploadSkillResponse {
  name: string;
  status: string;
  message: string;
}

// 删除技能响应接口
export interface DeleteSkillResponse {
  success: boolean;
  message: string;
}

// 启用/禁用技能请求接口
export interface ToggleSkillRequest {
  skill_name: string;
  type: 'enable' | 'disable';
}

// 启用/禁用技能响应接口
export interface ToggleSkillResponse {
  success: boolean;
  enabled_skills: string[];
  message: string;
}

/**
 * 获取Bot可用的技能列表
 * @param aiId 机器人ID（使用ai_id而不是botId，与API文档一致）
 * @param language 可选语言代码（如 ja/zh/en），用于翻译 skill 的 description/category
 * @returns 技能列表
 */
export const getSkillList = async (aiId: string, language?: string): Promise<SkillListResponse> => {
  const { get } = Http.use();

  const response = await get<SkillListResponse>(`/skill/${aiId}/list`, {
    params: language ? { language } : undefined,
  });

  return response.data;
};

/**
 * 上传技能包
 * @param aiId 机器人ID
 * @param file 技能包文件（ZIP格式，最大50MB）
 * @returns 上传结果
 */
export const uploadSkillPackage = async (aiId: string, file: File): Promise<UploadSkillResponse> => {
  const { post } = Http.use();

  const formData = new FormData();
  formData.append('file', file);

  const response = await post<UploadSkillResponse>(`/skill/${aiId}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * 删除技能
 * @param aiId 机器人ID
 * @param skillName 技能名称
 * @returns 删除结果
 */
export const deleteSkill = async (aiId: string, skillName: string): Promise<DeleteSkillResponse> => {
  const { delete: deleteHttp } = Http.use();

  const response = await deleteHttp<DeleteSkillResponse>(`/skill/${aiId}/remove`, {
    params: {
      skill_name: skillName,
    },
  });

  return response.data;
};

/**
 * 启用/禁用技能
 * @param aiId 机器人ID
 * @param skillName 技能名称
 * @param enabled 是否启用
 * @returns 启用/禁用结果
 */
export const toggleSkill = async (aiId: string, skillName: string, enabled: boolean): Promise<ToggleSkillResponse> => {
  const { post } = Http.use();

  const requestData: ToggleSkillRequest = {
    skill_name: skillName,
    type: enabled ? 'enable' : 'disable',
  };

  const response = await post<ToggleSkillResponse>(`/skill/${aiId}/enable`, requestData);

  return response.data;
};
